/**
 * Tiered reward bar — shared contract for editor, preview, and storefront runtime.
 */

import {
  CHECKOUT_TRACKING_KEYS,
  extractShopifyNumericId,
} from "./checkout-tracking.js";
import { matchOfferWhen } from "./resolve-effective-offer.js";

export const REWARD_BAR_MAX_TIERS = 3;

/** Fixed segment width (% of bar) per active tier count — markers sit at each segment end. */
export const REWARD_BAR_SEGMENT_WIDTH_BY_COUNT = Object.freeze({
  1: 100,
  2: 48,
  3: 32,
});

export function getRewardBarSegmentWidthPercent(tierCount) {
  const count = Math.min(
    Math.max(Number(tierCount) || 1, 1),
    REWARD_BAR_MAX_TIERS
  );
  return (
    REWARD_BAR_SEGMENT_WIDTH_BY_COUNT[count] ??
    REWARD_BAR_SEGMENT_WIDTH_BY_COUNT[3]
  );
}

export function getRewardBarTrackWidthPercent(tierCount) {
  const count = Math.min(
    Math.max(Number(tierCount) || 1, 1),
    REWARD_BAR_MAX_TIERS
  );
  return getRewardBarSegmentWidthPercent(count) * count;
}

export const REWARD_CALCULATION_TYPE = Object.freeze({
  CART_TOTAL: "cart_total",
  CART_QUANTITY: "cart_quantity",
  SPECIFIC_PRODUCTS: "specific_products",
});

/** Products that stay in the cart but do not count toward tier progress. */
export const REWARD_EXCLUDE_TYPE = Object.freeze({
  SPECIFIC_PRODUCTS: "specific_product",
  PRODUCTS_FROM_COLLECTION: "product_from_collection",
});

export function buildDefaultRewardExclude() {
  return {
    type: REWARD_EXCLUDE_TYPE.SPECIFIC_PRODUCTS,
    value: [],
  };
}

export function normalizeRewardExclude(setting = {}) {
  const raw = setting?.exclude;
  const type =
    raw?.type === REWARD_EXCLUDE_TYPE.PRODUCTS_FROM_COLLECTION
      ? REWARD_EXCLUDE_TYPE.PRODUCTS_FROM_COLLECTION
      : REWARD_EXCLUDE_TYPE.SPECIFIC_PRODUCTS;
  const value = Array.isArray(raw?.value) ? raw.value.filter(Boolean) : [];
  return { type, value };
}

/** Product ids the discount function can subtract. Collections stay unresolved here. */
export function rewardExcludeProductIds(setting = {}) {
  const exclude = normalizeRewardExclude(setting);
  if (exclude.type !== REWARD_EXCLUDE_TYPE.SPECIFIC_PRODUCTS) return [];
  return exclude.value
    .map((entry) =>
      extractShopifyNumericId(
        entry && typeof entry === "object" ? entry.id ?? entry.productId : entry
      )
    )
    .filter(Boolean);
}

export const REWARD_TIER_TYPE = Object.freeze({
  DISCOUNT: "discount",
  FREE_SHIPPING: "free_shipping",
  PRODUCT: "product",
});

export const REWARD_TIER_TYPE_OPTIONS = Object.freeze([
  { value: REWARD_TIER_TYPE.DISCOUNT, label: "Discount" },
  { value: REWARD_TIER_TYPE.FREE_SHIPPING, label: "Free shipping" },
  { value: REWARD_TIER_TYPE.PRODUCT, label: "Free gift" },
]);

/** Reward-type choices when calculation is Specific products (Free gift only). */
export const REWARD_TIER_TYPE_OPTIONS_SPECIFIC_PRODUCTS = Object.freeze([
  { value: REWARD_TIER_TYPE.PRODUCT, label: "Free gift" },
]);

export const REWARD_AMOUNT_PLACEHOLDER = "{AMOUNT}";

export const REWARD_TIER_PRODUCT_OFFER_MIN = 1;
export const REWARD_TIER_PRODUCT_OFFER_MAX = 10;
export const REWARD_TIER_PRODUCT_OFFER_DEFAULT = 1;

/**
 * Max products (or collections as picker rows) merchants can select for
 * reward-bar When / Products to offer pickers. UI-only; normalize does not
 * truncate when.value so published configs stay intact.
 */
export const REWARD_BAR_PRODUCT_SELECTION_MAX = 100;

export const REWARD_TIER_PRODUCT_OFFER_TYPE = "specific_product";

export const REWARD_TIER_MATCH_MIN_QTY_DEFAULT = 1;
export const REWARD_TIER_WHEN_TYPE_DEFAULT = "specific_product";
export const REWARD_TIER_FREE_GIFT_DISCOUNT_DEFAULT = 100;

export function isSpecificProductsRewardMode(setting = {}) {
  return (
    (setting?.calculationType || REWARD_CALCULATION_TYPE.CART_TOTAL) ===
    REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS
  );
}

export function buildDefaultRewardTierWhen() {
  return {
    type: REWARD_TIER_WHEN_TYPE_DEFAULT,
    value: [],
  };
}

const DEFAULT_TIER_COPY = Object.freeze({
  [REWARD_TIER_TYPE.DISCOUNT]: {
    description: "10% Discount",
    titleBeforeAchieving: `You're ${REWARD_AMOUNT_PLACEHOLDER} away from your discount!`,
  },
  [REWARD_TIER_TYPE.FREE_SHIPPING]: {
    description: "Free shipping",
    titleBeforeAchieving: `You're ${REWARD_AMOUNT_PLACEHOLDER} away from free shipping!`,
  },
  [REWARD_TIER_TYPE.PRODUCT]: {
    description: "Surprise Gift",
    titleBeforeAchieving: `You're ${REWARD_AMOUNT_PLACEHOLDER} away from your surprise gift!`,
  },
});

/** Default "before tier" titles when rewards use cart quantity (#). */
const DEFAULT_TIER_COPY_QUANTITY = Object.freeze({
  [REWARD_TIER_TYPE.DISCOUNT]: {
    titleBeforeAchieving: `Add ${REWARD_AMOUNT_PLACEHOLDER} more product & unlock 20% OFF`,
  },
  [REWARD_TIER_TYPE.FREE_SHIPPING]: {
    titleBeforeAchieving: `Add ${REWARD_AMOUNT_PLACEHOLDER} more product & unlock free shipping!`,
  },
  [REWARD_TIER_TYPE.PRODUCT]: {
    titleBeforeAchieving: `Add ${REWARD_AMOUNT_PLACEHOLDER} more product & unlock your surprise gift!`,
  },
});

/** Default titles when rewards unlock from specific products in cart. */
const DEFAULT_TIER_COPY_SPECIFIC_PRODUCTS = Object.freeze({
  [REWARD_TIER_TYPE.DISCOUNT]: {
    description: "10% Discount",
    titleBeforeAchieving: `Add ${REWARD_AMOUNT_PLACEHOLDER} more qualifying product(s) to unlock your discount!`,
  },
  [REWARD_TIER_TYPE.FREE_SHIPPING]: {
    description: "Free shipping",
    titleBeforeAchieving: `Add ${REWARD_AMOUNT_PLACEHOLDER} more qualifying product(s) to unlock free shipping!`,
  },
  [REWARD_TIER_TYPE.PRODUCT]: {
    description: "Free gift",
    titleBeforeAchieving: `Add ${REWARD_AMOUNT_PLACEHOLDER} more qualifying product(s) to unlock your free gift!`,
  },
});

export function getDefaultTitleBeforeAchieving(
  rewardType = REWARD_TIER_TYPE.DISCOUNT,
  calculationType = REWARD_CALCULATION_TYPE.CART_TOTAL
) {
  const type = rewardType || REWARD_TIER_TYPE.DISCOUNT;
  if (calculationType === REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS) {
    return (
      DEFAULT_TIER_COPY_SPECIFIC_PRODUCTS[type]?.titleBeforeAchieving ||
      DEFAULT_TIER_COPY_SPECIFIC_PRODUCTS[REWARD_TIER_TYPE.PRODUCT]
        .titleBeforeAchieving
    );
  }
  if (calculationType === REWARD_CALCULATION_TYPE.CART_QUANTITY) {
    return (
      DEFAULT_TIER_COPY_QUANTITY[type]?.titleBeforeAchieving ||
      DEFAULT_TIER_COPY_QUANTITY[REWARD_TIER_TYPE.DISCOUNT].titleBeforeAchieving
    );
  }
  return (
    DEFAULT_TIER_COPY[type]?.titleBeforeAchieving ||
    DEFAULT_TIER_COPY[REWARD_TIER_TYPE.DISCOUNT].titleBeforeAchieving
  );
}

/** When calculation type changes, refresh each tier's before-achieving title. */
export function applyRewardTitlesForCalculationType(tiers, calculationType) {
  const list = Array.isArray(tiers) ? tiers : [];
  if (calculationType === REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS) {
    return list.map((tier) => {
      const rewardType = tier?.rewardType || REWARD_TIER_TYPE.PRODUCT;
      return {
        ...tier,
        rewardType,
        isFreeGift:
          rewardType === REWARD_TIER_TYPE.PRODUCT
            ? tier?.isFreeGift !== false
            : false,
        when: tier?.when?.type
          ? {
              type: tier.when.type,
              value: Array.isArray(tier.when.value) ? tier.when.value : [],
            }
          : buildDefaultRewardTierWhen(),
        matchMinimumQuantity: Math.max(
          REWARD_TIER_MATCH_MIN_QTY_DEFAULT,
          Number(tier?.matchMinimumQuantity) || REWARD_TIER_MATCH_MIN_QTY_DEFAULT
        ),
        titleBeforeAchieving: getDefaultTitleBeforeAchieving(
          rewardType,
          calculationType
        ),
      };
    });
  }
  return list.map((tier) => ({
    ...tier,
    titleBeforeAchieving: getDefaultTitleBeforeAchieving(
      tier?.rewardType,
      calculationType
    ),
  }));
}
export function createRewardTierId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tier-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function buildDefaultRewardTier(
  rewardType = REWARD_TIER_TYPE.DISCOUNT,
  minimumAmount = 10,
  index = 0,
  calculationType = REWARD_CALCULATION_TYPE.CART_TOTAL
) {
  const isSpecific = calculationType === REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS;
  const resolvedType = rewardType || REWARD_TIER_TYPE.DISCOUNT;
  const copy =
    (isSpecific ? DEFAULT_TIER_COPY_SPECIFIC_PRODUCTS[resolvedType] : null) ||
    DEFAULT_TIER_COPY[resolvedType] ||
    DEFAULT_TIER_COPY[REWARD_TIER_TYPE.DISCOUNT];
  const tierBase = {
    id: createRewardTierId(),
    rewardType: resolvedType,
    minimumAmount: Number(minimumAmount) || 10 * (index + 1),
    description: copy.description,
    discount_type: "percentage",
    discount_value: 10,
    discount_message: "",
    product: null,
    products: [],
    productVariantId: "",
    productAddedKey: "",
    products_to_offer_limit: REWARD_TIER_PRODUCT_OFFER_DEFAULT,
    products_to_offer: {
      type: REWARD_TIER_PRODUCT_OFFER_TYPE,
      value: [],
    },
    when: buildDefaultRewardTierWhen(),
    matchMinimumQuantity: REWARD_TIER_MATCH_MIN_QTY_DEFAULT,
    // Product tiers: free-gift cart chrome vs normal line item
    isFreeGift: resolvedType === REWARD_TIER_TYPE.PRODUCT,
  };
  return {
    ...tierBase,
    titleBeforeAchieving: getDefaultTitleBeforeAchieving(
      resolvedType,
      calculationType
    ),
  };
}

export function buildDefaultRewardBarTiers() {
  return [
    buildDefaultRewardTier(REWARD_TIER_TYPE.DISCOUNT, 10, 0),
    buildDefaultRewardTier(REWARD_TIER_TYPE.FREE_SHIPPING, 50, 1),
    buildDefaultRewardTier(REWARD_TIER_TYPE.PRODUCT, 100, 2),
  ];
}

export const DEFAULT_FREE_GIFT_REOPEN_BANNER_TEXT =
  "🎁 Free gift unlocked — tap to choose!";
export const DEFAULT_FREE_GIFT_REOPEN_BANNER_BUTTON = "Choose gift";

/** Free-gift picker sheet copy (specific-products mode). */
export const DEFAULT_FREE_GIFT_PICKER_KICKER = "Free gift unlocked";
export const DEFAULT_FREE_GIFT_PICKER_TITLE = "Pick your gift";
export const DEFAULT_FREE_GIFT_PICKER_SUBTITLE =
  "Choose something special — on us. Add it before checkout.";
export const DEFAULT_FREE_GIFT_PICKER_CONFIRM_LABEL = "Add to cart ({count})";
export const DEFAULT_FREE_GIFT_PICKER_CONTINUE_LABEL = "Continue Shopping";
export const DEFAULT_FREE_GIFT_PICKER_COUNTER_LABEL =
  "{selected}/{limit} gift(s) selected";
export const DEFAULT_FREE_GIFT_PICKER_FREE_LABEL = "FREE";

export function buildDefaultRewardBarSetting(overrides = {}) {
  return {
    cartDrawerPart: "reward_bar",
    showOnEmptyCart: true,
    calculationType: REWARD_CALCULATION_TYPE.CART_TOTAL,
    usePreDiscountedPrices: false,
    barBackgroundColor: "#E2E2E2",
    barForegroundColor: "#000000",
    completeIconColor: "#ffffff",
    incompleteIconColor: "#4D4949",
    completedMessage: "All rewards unlocked!",
    headlineBackgroundColor: "#E2E2E2",
    completedMessageBackgroundColor: "#E2E2E2",
    descriptionFontSize: "10px",
    descriptionColor: "#4d4949",
    // Specific-products mode: banner to reopen free-gift picker after dismiss
    showFreeGiftReopenBanner: true,
    freeGiftReopenBannerText: DEFAULT_FREE_GIFT_REOPEN_BANNER_TEXT,
    freeGiftReopenBannerButtonText: DEFAULT_FREE_GIFT_REOPEN_BANNER_BUTTON,
    freeGiftReopenBannerBackground: "#FEF3C7",
    freeGiftReopenBannerColor: "#92400E",
    freeGiftPickerKicker: DEFAULT_FREE_GIFT_PICKER_KICKER,
    freeGiftPickerTitle: DEFAULT_FREE_GIFT_PICKER_TITLE,
    freeGiftPickerSubtitle: DEFAULT_FREE_GIFT_PICKER_SUBTITLE,
    freeGiftPickerConfirmLabel: DEFAULT_FREE_GIFT_PICKER_CONFIRM_LABEL,
    freeGiftPickerContinueLabel: DEFAULT_FREE_GIFT_PICKER_CONTINUE_LABEL,
    freeGiftPickerCounterLabel: DEFAULT_FREE_GIFT_PICKER_COUNTER_LABEL,
    freeGiftPickerFreeLabel: DEFAULT_FREE_GIFT_PICKER_FREE_LABEL,
    // Fire canvas confetti when a free-gift line is newly applied
    fireConfettiOnFreeGiftUnlock: true,
    exclude: buildDefaultRewardExclude(),
    tiers: buildDefaultRewardBarTiers(),
    customCSS: {
      desktop: {
        fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
        fontSize: "14px",
        fontWeight: "400",
        color: "#1a1a1a",
        textAlign: "center",
      },
      mobile: {
        fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
        fontSize: "13px",
        fontWeight: "400",
        color: "#1a1a1a",
        textAlign: "center",
      },
    },
    overrideCss: { desktop: "", mobile: "" },
    ...overrides,
  };
}

export function buildRewardBarSection(overrides = {}) {
  return {
    type: "REWARD_BAR",
    setting: buildDefaultRewardBarSetting(overrides.setting || {}),
  };
}

export function normalizeRewardBarTiers(tiers, calculationType) {
  if (!Array.isArray(tiers)) return buildDefaultRewardBarTiers();
  const calcType = calculationType || REWARD_CALCULATION_TYPE.CART_TOTAL;
  const normalized = tiers
    .slice(0, REWARD_BAR_MAX_TIERS)
    .map((tier, index) => {
      let rewardType = tier?.rewardType;
      if (!rewardType) {
        rewardType =
          calcType === REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS
            ? REWARD_TIER_TYPE.PRODUCT
            : REWARD_TIER_TYPE.DISCOUNT;
      }
      const defaults = buildDefaultRewardTier(
        rewardType,
        tier?.minimumAmount,
        index,
        calcType
      );
      const {
        automaticDiscountId: _legacyDiscountId,
        discount_details: _legacyDiscountDetails,
        ...tierFields
      } = tier || {};

      const whenType =
        tierFields?.when?.type || defaults.when.type || REWARD_TIER_WHEN_TYPE_DEFAULT;
      const whenValue = Array.isArray(tierFields?.when?.value)
        ? tierFields.when.value
        : [];

      return {
        ...defaults,
        ...tierFields,
        id: tierFields?.id || defaults.id,
        rewardType,
        minimumAmount: Number(tierFields?.minimumAmount ?? defaults.minimumAmount) || 0,
        description: tierFields?.description ?? defaults.description,
        discount_message:
          tierFields?.discount_message ?? defaults.discount_message,
        titleBeforeAchieving:
          tierFields?.titleBeforeAchieving ?? defaults.titleBeforeAchieving,
        discount_value: Number(tierFields?.discount_value ?? defaults.discount_value) || 0,
        products_to_offer_limit: Math.min(
          REWARD_TIER_PRODUCT_OFFER_MAX,
          Math.max(
            REWARD_TIER_PRODUCT_OFFER_MIN,
            Number(
              tierFields?.products_to_offer_limit ?? defaults.products_to_offer_limit
            ) || REWARD_TIER_PRODUCT_OFFER_DEFAULT
          )
        ),
        products_to_offer: {
          ...defaults.products_to_offer,
          ...(tierFields?.products_to_offer || {}),
        },
        when: {
          type: whenType,
          value: whenValue,
        },
        matchMinimumQuantity: Math.max(
          REWARD_TIER_MATCH_MIN_QTY_DEFAULT,
          Number(
            tierFields?.matchMinimumQuantity ?? defaults.matchMinimumQuantity
          ) || REWARD_TIER_MATCH_MIN_QTY_DEFAULT
        ),
        isFreeGift:
          rewardType === REWARD_TIER_TYPE.PRODUCT
            ? calcType === REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS
              ? true
              : tierFields?.isFreeGift == null
                ? true
                : Boolean(tierFields.isFreeGift)
            : false,
      };
    });

  while (normalized.length < 1) {
    normalized.push(
      buildDefaultRewardTier(
        calcType === REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS
          ? REWARD_TIER_TYPE.PRODUCT
          : REWARD_TIER_TYPE.DISCOUNT,
        10,
        normalized.length,
        calcType
      )
    );
  }
  return normalized;
}

export function getSortedRewardTiers(setting = {}) {
  const tiers = normalizeRewardBarTiers(
    setting.tiers,
    setting.calculationType
  );
  if (isSpecificProductsRewardMode(setting)) {
    return tiers;
  }
  return [...tiers].sort(
    (a, b) => Number(a.minimumAmount) - Number(b.minimumAmount)
  );
}

function isRewardTaggedCartItem(item) {
  const props = item?.properties;
  if (!props || typeof props !== "object") return false;
  return Boolean(props[CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID]);
}

/**
 * Map a Shopify /cart.js line to offer-rule match product shape.
 * /cart.js usually omits tags — pass productMetaById from storefront resolve.
 * @param {object} item
 * @param {{ productMetaById?: Record<string,{tags?:string[],vendor?:string}> }} [options]
 */
export function cartItemToRewardMatchProduct(item, options = {}) {
  if (!item || typeof item !== "object") return null;
  const productId =
    item.product_id ||
    item.productId ||
    extractShopifyNumericId(item.id) ||
    "";
  if (!productId && !item.title) return null;
  const metaKey = extractShopifyNumericId(productId) || String(productId);
  const metaMap = options.productMetaById || null;
  const meta =
    metaMap && metaKey
      ? typeof metaMap.get === "function"
        ? metaMap.get(metaKey)
        : metaMap[metaKey]
      : null;
  const lineTags = Array.isArray(item.tags) ? item.tags : [];
  return {
    id: productId,
    title: item.title || item.product_title || "",
    vendor: item.vendor || meta?.vendor || "",
    tags: lineTags.length ? lineTags : Array.isArray(meta?.tags) ? meta.tags : [],
    collections: Array.isArray(item.collections) ? item.collections : [],
    collection_ids: Array.isArray(item.collection_ids)
      ? item.collection_ids
      : [],
    handle: item.handle || "",
  };
}

/** True when this shopper line is listed on the reward bar's exclude rule. */
export function isRewardExcludedCartItem(item, setting = {}, options = {}) {
  if (isSpecificProductsRewardMode(setting)) return false;
  if (!item || isRewardTaggedCartItem(item)) return false;
  const exclude = normalizeRewardExclude(setting);
  if (!exclude.value.length) return false;
  const product = cartItemToRewardMatchProduct(item, options);
  if (!product) return false;
  return matchOfferWhen(exclude, product, options);
}

/**
 * Sum qty of non-reward cart lines that match tier.when.
 * @param {object} cart
 * @param {object} tier
 * @param {{ collectionProductIdsByKey?: Map|object, productMetaById?: object }} [options]
 */
export function getMatchingCartQuantity(cart, tier, options = {}) {
  const when = tier?.when;
  if (!when?.type) return 0;
  const items = Array.isArray(cart?.items) ? cart.items : [];
  let total = 0;
  for (const item of items) {
    if (isRewardTaggedCartItem(item)) continue;
    if (isRewardExcludedCartItem(item, options.rewardSetting, options)) continue;
    const product = cartItemToRewardMatchProduct(item, options);
    if (!product) continue;
    if (!matchOfferWhen(when, product, options)) continue;
    total += Math.max(0, Number(item.quantity) || 0);
  }
  return total;
}

/** Goal for "specific products" bars: matching cart qty, any reward type. */
export function isSpecificProductsGoalMet(cart, tier, options = {}) {
  if (!tier) return false;
  const minQty = Math.max(
    REWARD_TIER_MATCH_MIN_QTY_DEFAULT,
    Number(tier.matchMinimumQuantity) || REWARD_TIER_MATCH_MIN_QTY_DEFAULT
  );
  const when = tier.when;
  if (!when?.type) return false;
  if (Array.isArray(when.value) && when.value.length === 0) {
    // Empty product/collection list never matches (except all_product).
    if (when.type !== "all_product") return false;
  }
  return getMatchingCartQuantity(cart, tier, options) >= minQty;
}

export function tierMatchesSpecificProducts(cart, tier, options = {}) {
  if (!tier || tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) return false;
  return isSpecificProductsGoalMet(cart, tier, options);
}

export function isRewardTierAchieved(cart, tier, setting = {}, options = {}) {
  if (!tier) return false;
  const matchOptions = { ...options, rewardSetting: setting };
  if (isSpecificProductsRewardMode(setting)) {
    return isSpecificProductsGoalMet(cart, tier, matchOptions);
  }
  const currentValue = getCartRewardMetric(cart, setting, matchOptions);
  return currentValue + 0.0001 >= Number(tier.minimumAmount);
}

export function getCartRewardMetric(cart, setting = {}, options = {}) {
  if (!cart || typeof cart !== "object") return 0;

  const calculationType =
    setting.calculationType || REWARD_CALCULATION_TYPE.CART_TOTAL;

  // Reward-tagged free-gift lines must not count toward unlocking themselves.
  // Excluded products stay in the cart but do not move the tier.
  const shopperItems = (Array.isArray(cart.items) ? cart.items : []).filter(
    (item) => {
      if (isRewardTaggedCartItem(item)) return false;
      if (isRewardExcludedCartItem(item, setting, options)) return false;
      return true;
    }
  );

  if (calculationType === REWARD_CALCULATION_TYPE.CART_QUANTITY) {
    return shopperItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    );
  }

  if (setting.usePreDiscountedPrices) {
    const cents = shopperItems.reduce((sum, item) => {
      const line =
        Number(item.original_line_price ?? item.line_price ?? item.final_line_price ?? 0);
      return sum + (Number.isFinite(line) ? line : 0);
    }, 0);
    return cents / 100;
  }

  const cents = shopperItems.reduce((sum, item) => {
    const line = Number(item.final_line_price ?? item.line_price ?? 0);
    return sum + (Number.isFinite(line) ? line : 0);
  }, 0);
  return cents / 100;
}

export function formatRewardMetricValue(value, calculationType, formatMoney) {
  const numeric = Number(value) || 0;
  if (calculationType === REWARD_CALCULATION_TYPE.CART_QUANTITY) {
    const rounded = Math.max(0, Math.ceil(numeric));
    return String(rounded);
  }
  if (typeof formatMoney === "function") {
    return formatMoney(Math.round(numeric * 100));
  }
  return numeric.toFixed(2);
}

export function interpolateRewardTitle(template, amountText) {
  const raw = String(template || "");
  if (!raw.trim()) return "";
  return raw.split(REWARD_AMOUNT_PLACEHOLDER).join(amountText);
}

/**
 * @returns {{
 *   currentValue: number;
 *   tiers: Array;
 *   maxThreshold: number;
 *   progressPercent: number;
 *   achievedTierIds: Set<string>;
 *   nextTier: object | null;
 *   headlineHtml: string;
 *   allAchieved: boolean;
 * }}
 */
export function computeRewardBarState(cart, setting = {}, formatMoney, options = {}) {
  const tiers = getSortedRewardTiers(setting);
  const calculationType =
    setting.calculationType || REWARD_CALCULATION_TYPE.CART_TOTAL;
  const specificMode = calculationType === REWARD_CALCULATION_TYPE.SPECIFIC_PRODUCTS;
  const matchOptions = { ...options, rewardSetting: setting };

  const achievedTierIds = new Set();
  tiers.forEach((tier) => {
    if (isRewardTierAchieved(cart, tier, setting, matchOptions)) {
      achievedTierIds.add(tier.id);
    }
  });

  let currentValue;
  let maxThreshold;
  let progressPercent;
  let visualProgressPercent;

  if (specificMode) {
    currentValue = achievedTierIds.size;
    maxThreshold = Math.max(tiers.length, 1);
    progressPercent = Math.min(100, (currentValue / maxThreshold) * 100);
    visualProgressPercent = getRewardBarVisualProgressPercent(
      tiers.map((tier, index) => ({
        ...tier,
        minimumAmount: index + 1,
      })),
      currentValue
    );
  } else {
    currentValue = getCartRewardMetric(cart, setting, matchOptions);
    maxThreshold = Math.max(
      ...tiers.map((t) => Number(t.minimumAmount) || 0),
      0.0001
    );
    progressPercent = Math.min(100, (currentValue / maxThreshold) * 100);
    visualProgressPercent = getRewardBarVisualProgressPercent(
      tiers,
      currentValue
    );
  }

  const nextTier = tiers.find((tier) => !achievedTierIds.has(tier.id)) || null;
  const allAchieved = tiers.length > 0 && tiers.every((t) => achievedTierIds.has(t.id));

  let headlineHtml = "";
  if (allAchieved) {
    headlineHtml = String(setting.completedMessage || "All rewards unlocked!");
  } else if (nextTier) {
    let remaining;
    let amountCalcType = calculationType;
    if (specificMode) {
      const matchedQty = getMatchingCartQuantity(cart, nextTier, matchOptions);
      const minQty = Math.max(
        REWARD_TIER_MATCH_MIN_QTY_DEFAULT,
        Number(nextTier.matchMinimumQuantity) || REWARD_TIER_MATCH_MIN_QTY_DEFAULT
      );
      remaining = Math.max(0, minQty - matchedQty);
      amountCalcType = REWARD_CALCULATION_TYPE.CART_QUANTITY;
    } else {
      remaining = Math.max(0, Number(nextTier.minimumAmount) - currentValue);
    }
    const amountText = formatRewardMetricValue(
      remaining,
      amountCalcType,
      formatMoney
    );
    const plain = interpolateRewardTitle(nextTier.titleBeforeAchieving, amountText);
    headlineHtml = plain.replace(
      new RegExp(amountText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
      `<strong>${amountText}</strong>`
    );
  }

  return {
    currentValue,
    tiers,
    maxThreshold,
    progressPercent,
    visualProgressPercent,
    achievedTierIds,
    nextTier,
    headlineHtml,
    allAchieved,
    calculationType,
  };
}

export function getTierMarkerPercent(tier, maxThreshold) {
  const threshold = Math.max(Number(maxThreshold) || 1, 0.0001);
  return Math.min(100, (Number(tier.minimumAmount) / threshold) * 100);
}

/** Tier markers at fixed segment ends: 32/64/96% (3), 48/96% (2), 100% (1). */
export function getTierMarkerPercentEqualSegments(tierIndex, tierCount) {
  const count = Math.min(
    Math.max(Number(tierCount) || 1, 1),
    REWARD_BAR_MAX_TIERS
  );
  if (count <= 0) return 0;
  const segmentWidth = getRewardBarSegmentWidthPercent(count);
  return (tierIndex + 1) * segmentWidth;
}

/** @deprecated Use getTierMarkerPercentEqualSegments */
export function getTierMarkerPercentEven(tierIndex, tierCount) {
  return getTierMarkerPercentEqualSegments(tierIndex, tierCount);
}

/**
 * Map cart progress onto fixed-width bar segments (each tier sits at a segment boundary).
 */
export function getRewardBarVisualProgressPercent(tiers, currentValue) {
  const sorted = [...tiers].sort(
    (a, b) => Number(a.minimumAmount) - Number(b.minimumAmount)
  );
  const count = sorted.length;
  const value = Number(currentValue) || 0;

  if (count === 0) return 0;

  const segmentWidth = getRewardBarSegmentWidthPercent(count);
  const trackWidth = getRewardBarTrackWidthPercent(count);
  const markerAt = (index) => (index + 1) * segmentWidth;
  const segmentStart = (index) => index * segmentWidth;

  if (count === 1) {
    const min = Math.max(Number(sorted[0].minimumAmount) || 1, 0.0001);
    return Math.min(trackWidth, (value / min) * trackWidth);
  }

  const lastMin = Number(sorted[count - 1].minimumAmount);

  if (value + 0.0001 >= lastMin) return trackWidth;

  for (let i = 0; i < count; i += 1) {
    const tierMin = Number(sorted[i].minimumAmount);
    if (value + 0.0001 < tierMin) {
      const prevMin = i === 0 ? 0 : Number(sorted[i - 1].minimumAmount);
      const startPos = segmentStart(i);
      const endPos = markerAt(i);
      const span = tierMin - prevMin;
      const ratio =
        span > 0
          ? Math.max(0, Math.min(1, (value - prevMin) / span))
          : 0;

      return startPos + ratio * (endPos - startPos);
    }
  }

  return trackWidth;
}

export function findRewardBarSection(sections) {
  if (!Array.isArray(sections)) return null;
  return (
    sections.find(
      (s) => s?.type === "REWARD_BAR" || s?.setting?.cartDrawerPart === "reward_bar"
    ) || null
  );
}

/**
 * Default bill label when the merchant has not written a message or description.
 * Percentage tiers read as "5% discount" so the row is not an unnamed coupon.
 */
export function formatRewardDiscountRateLabel(tier) {
  const type = normalizeRewardDiscountType(tier?.discount_type);
  const value = Number(tier?.discount_value);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (type !== "percentage") return "";
  const shown = Number.isInteger(value) ? String(value) : String(value);
  return `${shown}% discount`;
}

/**
 * Label shown in cart Bill Summary / Shopify discount message for a reward tier.
 * Prefers "Discount message", then reward-bar "Reward description", then "5% discount".
 */
export function getRewardTierBillLabel(tier, fallback = "Discount") {
  const message = String(tier?.discount_message || "").trim();
  if (message) return message;
  const description = String(tier?.description || "").trim();
  if (description) return description;
  return formatRewardDiscountRateLabel(tier) || fallback;
}

/**
 * Best achieved order-level discount tier (highest minimum), if any.
 */
export function getBestAchievedRewardOrderDiscountTier(cart, setting = {}) {
  const state = computeRewardBarState(cart, setting);
  const achievedDiscountTiers = state.tiers.filter(
    (tier) =>
      tier.rewardType === REWARD_TIER_TYPE.DISCOUNT &&
      state.achievedTierIds.has(tier.id)
  );
  if (!achievedDiscountTiers.length) return null;
  return achievedDiscountTiers.reduce((best, tier) =>
    Number(tier.minimumAmount) > Number(best?.minimumAmount || 0) ? tier : best
  , null);
}

/**
 * Shopify automatic discount payloads for reward bar tiers (editor publish + server).
 */
export function buildRewardTierDiscountPayload(tier, popupTitle = "Cart reward") {
  const minimum = Number(tier.minimumAmount) || 0;
  const titleBase = `${popupTitle} — ${getRewardTierBillLabel(tier, "Reward")}`.trim();

  if (tier.rewardType === REWARD_TIER_TYPE.FREE_SHIPPING) {
    return {
      automaticFreeShippingDiscount: {
        title: titleBase,
        startsAt: new Date().toISOString(),
        minimumRequirement: {
          subtotal: {
            greaterThanOrEqualToSubtotal: String(minimum),
          },
        },
        destination: {
          all: true,
        },
      },
    };
  }

  if (tier.rewardType !== REWARD_TIER_TYPE.DISCOUNT) {
    return null;
  }

  const discountType = tier.discount_type || "percentage";
  const discountValue = Number(tier.discount_value) || 0;
  if (!discountValue) return null;

  return {
    automaticBasicDiscount: {
      title: titleBase,
      startsAt: new Date().toISOString(),
      minimumRequirement: {
        subtotal: {
          greaterThanOrEqualToSubtotal: String(minimum),
        },
      },
      customerGets: {
        value:
          discountType === "percentage"
            ? { percentage: discountValue / 100 }
            : {
                discountAmount: {
                  amount: String(discountValue),
                  appliesOnEachItem: false,
                },
              },
        items: { all: true },
      },
      combinesWith: {
        productDiscounts: true,
        shippingDiscounts: true,
        orderDiscounts: true,
      },
    },
  };
}

export function tierNeedsShopifyDiscount(tier) {
  if (!tier) return false;
  if (tier.rewardType === REWARD_TIER_TYPE.FREE_SHIPPING) return true;
  if (tier.rewardType === REWARD_TIER_TYPE.DISCOUNT) {
    const value = Number(tier.discount_value);
    return Boolean(tier.discount_type) && Number.isFinite(value) && value > 0;
  }
  return false;
}

export function normalizeRewardDiscountType(type) {
  const normalized = String(type || "").toLowerCase().trim();
  if (normalized === "amount" || normalized === "fixed" || normalized === "fixed_amount") {
    return "amount";
  }
  if (normalized === "percentage" || normalized === "percent") {
    return "percentage";
  }
  return normalized || "percentage";
}

export function hasActiveRewardProductDiscount(product) {
  const value = Number(product?.discount_value ?? product?.value);
  return (
    Boolean(product?.discount_type || product?.type) &&
    Number.isFinite(value) &&
    value > 0
  );
}

export function computeRewardDiscountCents(subtotalCents, discountType, discountValue) {
  const subtotal = Math.max(0, Number(subtotalCents) || 0);
  const value = Number(discountValue) || 0;
  if (!value || subtotal <= 0) return 0;

  const type = normalizeRewardDiscountType(discountType);
  if (type === "amount") {
    return Math.min(Math.round(value * 100), subtotal);
  }
  return Math.round((subtotal * value) / 100);
}

export function resolveRewardProductVariantId(product, tier) {
  return extractShopifyNumericId(
    product?.variant_id ||
      product?.variants?.[0]?.id ||
      tier?.productVariantId ||
      ""
  );
}

function getCartLineRewardTierId(item) {
  const props = item?.properties;
  if (!props || typeof props !== "object") return "";
  return String(props[CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID] || "");
}

/**
 * Find the cart line that should receive a reward-product discount.
 * Prefers lines tagged with `_weupsell_rewardTierId` for this tier.
 */
export function findRewardProductCartLine(cart, product, tier) {
  const tierId = String(tier?.id || "");
  const productId = extractShopifyNumericId(product?.id);
  const variantId = resolveRewardProductVariantId(product, tier);

  for (const item of cart?.items || []) {
    const lineTierId = getCartLineRewardTierId(item);
    if (!lineTierId || lineTierId !== tierId) continue;
    if (variantId && String(item.variant_id) === variantId) return item;
    if (productId && String(item.product_id) === productId) return item;
  }

  return null;
}

function rewardProductDiscountBaseCents(cartItem) {
  const quantity = Math.max(1, Number(cartItem?.quantity) || 1);
  const originalLine = Number(
    cartItem?.original_line_price ?? cartItem?.line_price ?? 0
  );
  if (originalLine > 0) return originalLine;

  const unitOriginal = Number(cartItem?.original_price ?? cartItem?.price) || 0;
  return unitOriginal * quantity;
}

/**
 * True only when Shopify (or another allocator) already zeroed the line /
 * covered the expected reward discount — not when a catalog sale merely
 * lowered original_line_price → final_line_price.
 */
function isRewardLineDiscountFullyApplied(cartItem, expectedDiscountCents) {
  const base = rewardProductDiscountBaseCents(cartItem);
  const finalLine = Number(
    cartItem?.final_line_price ?? cartItem?.line_price ?? base
  );
  if (!Number.isFinite(finalLine)) return false;
  if (finalLine <= 0) return true;

  const expected = Math.max(0, Number(expectedDiscountCents) || 0);
  if (expected <= 0) return false;

  const applied = Math.max(0, base - finalLine);
  // Allow 1¢ rounding; require essentially the full reward cut.
  return applied + 1 >= expected;
}

/**
 * Resolve display pricing for a reward-product / free-gift cart line.
 * Overlays configured tier product discount when Shopify has not applied it yet
 * (AJAX cart often still shows the selling price).
 *
 * @returns {{
 *   finalLine: number,
 *   originalLine: number,
 *   showDiscount: boolean,
 *   isFree: boolean,
 *   estimatedDiscountCents: number,
 * } | null}
 */
export function resolveRewardGiftLineDisplayPricing(
  cartItem,
  product,
  { forceFreeGift = false } = {}
) {
  if (!cartItem || !product) return null;

  // Free-gift lines always display as 100% off (tag/collection/vendor pools
  // may omit discount_value on resolved stubs; Shopify Function may lag).
  const type = forceFreeGift
    ? "percentage"
    : product.discount_type || product.type || "";
  let value = Number(product.discount_value ?? product.value);
  if (forceFreeGift) {
    value = REWARD_TIER_FREE_GIFT_DISCOUNT_DEFAULT;
  } else if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  if (!type) return null;

  const baseCents = rewardProductDiscountBaseCents(cartItem);
  if (baseCents <= 0) return null;

  const estimatedDiscountCents = computeRewardDiscountCents(
    baseCents,
    type || "percentage",
    value
  );
  if (estimatedDiscountCents <= 0) return null;

  const shopifyFinal = Number(
    cartItem?.final_line_price ?? cartItem?.line_price ?? baseCents
  );
  const estimatedFinal = Math.max(0, baseCents - estimatedDiscountCents);
  const applied = isRewardLineDiscountFullyApplied(
    cartItem,
    estimatedDiscountCents
  );
  const safeShopifyFinal = Number.isFinite(shopifyFinal)
    ? shopifyFinal
    : baseCents;
  // Free-gift lines: show FREE / 100% off immediately. Checkout still depends on
  // the discount Function metafield (expanded collection/tag/vendor/title pools).
  // Non-free reward discounts stay conservative until Shopify applies them.
  const useEstimate = applied || forceFreeGift;
  const finalLine = useEstimate
    ? Math.min(safeShopifyFinal, estimatedFinal)
    : safeShopifyFinal;
  const isFree = finalLine <= 0;

  return {
    finalLine,
    originalLine: baseCents,
    showDiscount: useEstimate || finalLine < baseCents,
    isFree,
    estimatedDiscountCents,
    pendingDiscount: !applied && estimatedDiscountCents > 0,
  };
}

/**
 * Product-tier discount rows only (for merging with order-level Shopify discounts).
 */
export function estimateRewardProductDiscountRows(cart, setting = {}) {
  const state = computeRewardBarState(cart, setting);
  const rows = [];

  for (const tier of state.tiers) {
    if (tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) continue;
    if (!state.achievedTierIds.has(tier.id)) continue;

    for (const product of tier.products || []) {
      const discountType =
        product?.discount_type ||
        product?.type ||
        (tier.isFreeGift !== false ? "percentage" : "");
      let discountValue = Number(product?.discount_value ?? product?.value);
      if (!Number.isFinite(discountValue) || discountValue <= 0) {
        if (tier.isFreeGift === false) continue;
        discountValue = REWARD_TIER_FREE_GIFT_DISCOUNT_DEFAULT;
      }
      if (!discountType) continue;

      const cartItem = findRewardProductCartLine(cart, product, tier);
      if (!cartItem) continue;

      const lineCents = rewardProductDiscountBaseCents(cartItem);
      const amountCents = computeRewardDiscountCents(
        lineCents,
        discountType,
        discountValue
      );
      if (amountCents <= 0) continue;
      if (isRewardLineDiscountFullyApplied(cartItem, amountCents)) continue;

      rows.push({
        title:
          product.discount_message ||
          tier.description ||
          product.title ||
          "Reward product",
        amountCents,
      });
    }
  }

  return rows;
}

/**
 * Order-tier discount rows only (cart drawer footer — excludes product reward lines).
 */
function eligibleRewardOrderSubtotalCents(cart, setting = {}) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return items.reduce((sum, item) => {
    if (isRewardTaggedCartItem(item)) return sum;
    if (isRewardExcludedCartItem(item, setting)) return sum;
    const line = Number(item?.final_line_price ?? item?.line_price ?? 0);
    return sum + (Number.isFinite(line) ? line : 0);
  }, 0);
}

export function estimateRewardOrderDiscountRows(cart, setting = {}) {
  const rows = [];
  const subtotalCents = eligibleRewardOrderSubtotalCents(cart, setting);
  const bestTier = getBestAchievedRewardOrderDiscountTier(cart, setting);

  if (bestTier && subtotalCents > 0) {
    const amountCents = computeRewardDiscountCents(
      subtotalCents,
      bestTier.discount_type,
      bestTier.discount_value
    );
    if (amountCents > 0) {
      rows.push({
        title: getRewardTierBillLabel(bestTier),
        amountCents,
      });
    }
  }

  return rows;
}

/**
 * Estimate reward-bar discount rows for cart drawer preview (cart.js shape).
 * Used when Shopify has not applied automatic discounts yet (editor / AJAX cart).
 */
export function estimateRewardBarDiscountRows(cart, setting = {}) {
  const rows = estimateRewardOrderDiscountRows(cart, setting);
  estimateRewardProductDiscountRows(cart, setting).forEach((row) => rows.push(row));
  return rows;
}
